import { useEffect } from "react";

import { useLocale } from "@/hooks/use-locale";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/auth";

/**
 * Push the resolved locale into the signed-in user's auth metadata, so the two emails GoTrue sends
 * are in their language too (POS2-1160).
 *
 * GoTrue renders the signup-confirmation and password-reset emails itself, and the only
 * per-recipient value its templates can read is the recipient's own `raw_user_meta_data` — there is
 * no path from a Go template to `user_preferences.locale` or `organisations.default_locale`. So the
 * chain's answer has to be copied there, and this is what keeps the copy current: a language change
 * writes on the same interaction, and an organisation changing its default reaches each member on
 * their next boot. `updateUser` with `data` needs only the user's own session — no service role, no
 * confirmation email, no reauthentication.
 *
 * **Its own module, not a second export from `use-locale`** — and that is not a style preference.
 * `use-translation` reaches `use-locale` through `I18nProvider`, so importing the Supabase client
 * into that module made it a transitive dependency of every component that calls `t()`. Since
 * `lib/supabase` throws at module load when the environment is absent, the whole frontend suite went
 * red in CI on files with nothing to do with locale — a `.env` on the developer's machine is the
 * only reason it passed there. Keep the auth client out of the i18n graph.
 *
 * Mounted in `AppLayout` for the same reason `useApplyBrand` is: the pre-auth screens sit outside
 * it, and before sign-in there is no session to write to. Getting the locale into a *new* user's
 * metadata is the signup form's job — see `pages/invite/accept.tsx`, which reads it off the
 * invitation link.
 *
 * Deliberately **not** gated on the locale chain's two queries having settled. Gating would mean
 * asking a disabled query whether it is done, which is never true for an org-less labeller account
 * and would leave those users out. The cost of not gating is bounded and small: on a boot where the
 * seeded locale differs from the one the chain settles on — a browser that has never rendered the
 * app before — this writes twice. In the steady state the seed is already the answer and it writes
 * nothing.
 */
export function useEmailLocaleMirror() {
  const { locale } = useLocale();
  const user = useAuthStore((s) => s.user);
  const inMetadata = user?.user_metadata.locale as string | undefined;

  useEffect(() => {
    if (!user || inMetadata === locale) return;
    void supabase.auth.updateUser({ data: { locale } });
  }, [user, inMetadata, locale]);
}
